/**
 * Handbook comment media uploads (Supabase images, YouTube shorts).
 * Each POST creates a new `comments` row; many comment rows are allowed per handbook_entry.
 * Image object keys must be unique per file (see makeHandbookCommentImageStoragePath).
 */
import { getSupabaseClient, getSupabaseConfigHint, getCommentImagesBucketName } from './supabase';
import { uploadYoutubeBinaryFromBase64 } from './youtubeBinaryUpload';

function sanitizeUploadFilename(name) {
  const raw = String(name || '').replace(/^.*[/\\]/, '');
  const cleaned = raw.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return (cleaned || 'file').slice(0, 180);
}

/** Ensures each Supabase object key is unique (same bucket; many comments per parent). */
function makeHandbookCommentImageStoragePath(parentId, safeFileName, { prefix = 'entry' } = {}) {
  const t = Date.now();
  const r =
    typeof globalThis !== 'undefined' &&
    globalThis.crypto &&
    typeof globalThis.crypto.randomUUID === 'function'
      ? globalThis.crypto.randomUUID().replace(/-/g, '').slice(0, 16)
      : `${Math.random().toString(36).slice(2, 12)}${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}_${parentId}_${t}_${r}_${safeFileName}`;
}

function normalizeCourseImageForUpload(fileName, mimeType) {
  let safe = sanitizeUploadFilename(fileName);
  const hasImgExt = /\.(jpe?g|png|gif|webp|heic)$/i.test(safe);
  let ct = (mimeType && String(mimeType).trim()) || '';
  if (!ct.startsWith('image/')) {
    const lower = safe.toLowerCase();
    if (lower.endsWith('.png')) ct = 'image/png';
    else if (lower.endsWith('.webp')) ct = 'image/webp';
    else if (lower.endsWith('.gif')) ct = 'image/gif';
    else if (lower.endsWith('.heic')) ct = 'image/heic';
    else ct = 'image/jpeg';
  }
  if (!ct.startsWith('image/') || ct === 'application/octet-stream') {
    ct = 'image/jpeg';
  }
  if (!hasImgExt) {
    const ext =
      ct === 'image/png'
        ? 'png'
        : ct === 'image/webp'
          ? 'webp'
          : ct === 'image/gif'
            ? 'gif'
            : ct === 'image/heic'
              ? 'heic'
              : 'jpg';
    const base = safe.replace(/\.[^.]+$/, '').replace(/^_+|_+$/g, '');
    safe = `${base || 'image'}.${ext}`;
  }
  return { safe, contentType: ct };
}

export async function uriToArrayBuffer(uri) {
  const res = await fetch(uri);
  if (!res.ok) throw new Error(`Could not read file (${res.status})`);
  return res.arrayBuffer();
}

/**
 * @param {{ entryId?: number, noteId?: number, pendingImage: { uri: string, fileName: string, mimeType?: string|null } }} p
 * @returns {Promise<string[]>}
 */
export async function uploadHandbookCommentImageFromPicker({ entryId, noteId, pendingImage }) {
  const parentId = noteId != null ? noteId : entryId;
  const prefix = noteId != null ? 'note' : 'entry';
  if (parentId == null) return [];
  if (!pendingImage?.uri) return [];
  const dbg = (label, payload) =>
    console.log(`[Handbook comment image] ${label}`, typeof payload === 'object' ? payload : String(payload));
  const supabase = getSupabaseClient();
  if (!supabase) {
    dbg('ERROR no Supabase client', getSupabaseConfigHint());
    throw new Error(getSupabaseConfigHint().join('\n') || 'Supabase is not configured.');
  }
  const bucket = getCommentImagesBucketName();
  const { safe, contentType } = normalizeCourseImageForUpload(pendingImage.fileName, pendingImage.mimeType);
  const path = makeHandbookCommentImageStoragePath(parentId, safe, { prefix });
  dbg('start', {
    parentId,
    prefix,
    bucket,
    path,
    contentType,
    fileName: pendingImage.fileName,
    uriPrefix: String(pendingImage.uri).slice(0, 80),
  });
  let body;
  try {
    body = await uriToArrayBuffer(pendingImage.uri);
  } catch (e) {
    console.error('[Handbook comment image] uriToArrayBuffer failed', e?.message || e, {
      uriPrefix: String(pendingImage.uri).slice(0, 100),
    });
    throw e;
  }
  dbg('arrayBuffer', { byteLength: body?.byteLength ?? 0 });
  if (!body || body.byteLength === 0) throw new Error('Empty image file.');
  const { data: upData, error } = await supabase.storage.from(bucket).upload(path, body, {
    contentType,
    upsert: false,
  });
  if (error) {
    console.error('[Handbook comment image] storage.upload failed', {
      message: error.message,
      name: error.name,
      // supabase-js may attach status / details
      ...error,
    });
    throw new Error(error.message || 'Image upload failed');
  }
  dbg('upload ok', { path: upData?.path ?? path });
  const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
  const url = pub?.publicUrl ? String(pub.publicUrl).trim().replace(/^http:\/\//i, 'https://') : '';
  if (!url) {
    dbg('ERROR empty getPublicUrl', { pub });
    throw new Error('No public URL from storage');
  }
  dbg('public URL', url.slice(0, 120));
  return [url];
}

/**
 * @param {{ fileBase64: string, videoTitle: string, formTitle?: string }} p
 * @returns {Promise<string[]>}
 */
export async function uploadHandbookCommentYoutubeFromPicker({
  fileBase64,
  videoTitle,
  formTitle,
  contextLabel = 'Handbook entry comment',
}) {
  if (!fileBase64 || !String(videoTitle || '').trim()) {
    throw new Error('Video title is required to upload.');
  }
  const y = await uploadYoutubeBinaryFromBase64({
    fileBase64,
    title: String(videoTitle).trim(),
    description: `${contextLabel}: ${formTitle || 'Comment'}`,
    privacyStatus: 'unlisted',
    isShort: true,
    account_key: 'default',
  });
  return [y.youtubeUrl];
}
